import { useRef, useState } from "react";

export default function AudioRecorderPopup({ isOpen, onClose, onSave }) {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const [text, setText] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recording, setRecording] = useState(false);

  if (!isOpen) return null;

  // 🎙 START RECORDING
  const startRecording = async () => {
    if (text.trim()) return; // text already used

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    streamRef.current = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    let mimeType = "audio/webm";

    // ✅ Safari / iOS does NOT support audio/webm
    if (isIOS) {
      if (MediaRecorder.isTypeSupported("audio/mp4")) {
        mimeType = "audio/mp4";
      } else if (MediaRecorder.isTypeSupported("audio/wav")) {
        mimeType = "audio/wav";
      }
    }

    mediaRecorderRef.current = new MediaRecorder(streamRef.current, {
      mimeType,
    });

    chunksRef.current = [];

    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });

      if (blob.size === 0) {
        console.error("Recording failed: empty audio blob");
        return;
      }

      const url = URL.createObjectURL(blob);

      setAudioBlob(blob);   // ✅ upload this
      setAudioUrl(url);     // 👀 preview only
    };

    mediaRecorderRef.current.start();
    setRecording(true);
  };


  // ⏹ STOP RECORDING
  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;

    if (mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
    }

    setRecording(false);
  };

  // 🔁 RESET
  const reset = () => {
    setText("");
    setAudioUrl(null);
    setAudioBlob(null);
    setRecording(false);
  };

  // 💾 SAVE (ONE SOURCE ONLY)
  const handleSave = () => {
    if (audioBlob) {
      onSave({ type: "audio", value: audioBlob, url: audioUrl });
    } else if (text.trim()) {
      onSave({ type: "text", value: text });
    }
    onClose();
    reset();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999,
      }}
    >
      <div
        style={{
          width: 330,
          backgroundColor: "#fff",
          borderRadius: 12,
          padding: 16,
        }}
      >
        <h4>Add Instruction</h4>

        {/* ✍️ TEXT INPUT */}
        <input
          type="text"
          placeholder="Type instruction..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={recording || audioUrl}
          style={{
            width: "100%",
            padding: 8,
            fontSize: 12,
            borderRadius: 6,
            border: "1px solid #ccc",
            opacity: recording || audioUrl ? 0.5 : 1,
          }}
        />

        {/* OR */}
        <div
          style={{
            textAlign: "center",
            fontSize: 11,
            color: "#999",
            margin: "8px 0",
          }}
        >
          — OR —
        </div>

        {/* 🎙 RECORD */}
        {!audioUrl && (
          <button
            onClick={recording ? stopRecording : startRecording}
            disabled={!!text.trim()}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 8,
              border: "none",
              backgroundColor: recording ? "#ff5252" : "#4caf50",
              color: "white",
              cursor: text.trim() ? "not-allowed" : "pointer",
              opacity: text.trim() ? 0.6 : 1,
            }}
          >
            {recording ? "⏹ Stop Recording" : "🎙 Record Voice"}
          </button>
        )}

        {/* 🎧 PREVIEW */}
        {audioUrl && (
          <div style={{ marginTop: 10 }}>
            <audio controls src={audioUrl} style={{ width: "100%" }} />
            <button
              onClick={reset}
              style={{
                width: "100%",
                marginTop: 6,
                padding: 6,
                border: "1px dashed #999",
                background: "transparent",
              }}
            >
              🔁 Re-record / Switch to Text
            </button>
          </div>
        )}

        {/* ACTIONS */}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => {
              reset();
              onClose();
            }}
            style={{
              flex: 1,
              padding: 8,
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={!text.trim() && !audioBlob}
            style={{
              flex: 1,
              padding: 8,
              borderRadius: 6,
              border: "none",
              backgroundColor:
                text.trim() || audioBlob ? "#4caf50" : "#ccc",
              color: "white",
              cursor:
                text.trim() || audioBlob ? "pointer" : "not-allowed",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
